import { type FormEvent, useEffect, useMemo, useState } from 'react';
import type { BookmarkNode, BrowserSettings, InstalledExtension, InternalPage as InternalPageName, OfflinePageRecord, SearchProvider, SearchResponse } from '../types';
import { deleteOfflinePage, listOfflinePages } from '../lib/db';
import { HistoryPage } from './HistoryPage';
import { BookmarkManager } from './BookmarkManager';
import { ExtensionsPage } from './ExtensionsPage';
import { PasswordManager } from './PasswordManager';
import { SettingsPage } from './SettingsPage';
import { HtmlSandbox } from './HtmlSandbox';
import { SEARCH_ENGINES } from '../constants';
import { searchApiUrl, toDisplayUrl } from '../lib/urls';

interface Props {
  page: InternalPageName;
  currentUrl: string;
  bookmarks: BookmarkNode;
  settings: BrowserSettings;
  extensions: InstalledExtension[];
  onBookmarksChange: (root: BookmarkNode) => void;
  onSettingsChange: (settings: BrowserSettings) => void;
  onExtensionsChange: (extensions: InstalledExtension[]) => void;
  onOpen: (url: string) => void;
}

function HomePage({ settings, onOpen }: { settings: BrowserSettings; onOpen: (url: string) => void }) {
  const [query, setQuery] = useState('');
  const [offlinePages, setOfflinePages] = useState<OfflinePageRecord[]>([]);
  const reload = () => listOfflinePages().then(setOfflinePages);
  useEffect(() => { reload(); }, []);
  function submitSearch(event: FormEvent) {
    event.preventDefault();
    if (query.trim()) onOpen(query.trim());
  }
  const activeEngine = SEARCH_ENGINES[settings.searchProvider]?.label || 'Custom';
  const quickLinks = [
    ['Settings', 'websurf://settings'],
    ['History', 'websurf://history'],
    ['Bookmarks', 'websurf://bookmarks'],
    ['Extensions', 'websurf://extensions'],
    ['Passwords', 'websurf://passwords'],
    ['HTML Sandbox', 'websurf://sandbox']
  ];
  return (
    <section className="home-page">
      <div className="hero-card brand-hero">
        <img className="brand-logo-card" src="/icons/websurf-logo-white-card.png" alt="WebSurf Search Browser logo" />
        <p className="eyebrow">Search Browser</p>
        <h1>WebSurf</h1>
        <p>A Chrome-inspired in-app browser shell with smart rendering, native search results, enhanced proxy rendering, offline pages, tab management, saved snapshots, internal tools, and a local encrypted vault.</p>
        <form className="home-search" onSubmit={submitSearch}>
          <span>{activeEngine}</span>
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search or enter a website" autoComplete="off" />
          <button type="submit">Search</button>
        </form>
      </div>
      <div className="quick-grid">
        {quickLinks.map(([label, url]) => <button key={url} onClick={() => onOpen(url)}>{label}<span>{url}</span></button>)}
      </div>
      <div className="list-card offline-list">
        <header><h2>Available Offline</h2><button onClick={reload}>Refresh</button></header>
        {offlinePages.map((page) => (
          <div className="list-row" key={page.id}>
            <div className="row-main"><strong>{page.title}</strong><span>{page.url}</span><small>{Math.round(page.byteSize / 1024).toLocaleString()} KB saved</small></div>
            <button onClick={() => onOpen(page.url)}>Open</button>
            <button onClick={() => deleteOfflinePage(page.id).then(reload)}>Delete</button>
          </div>
        ))}
        {offlinePages.length === 0 && <p className="empty-copy">Use “Make Page Available Offline” from the toolbar to save pages here.</p>}
      </div>
    </section>
  );
}

function searchParamsFromWebSurfUrl(url: string): URLSearchParams {
  const query = url.includes('?') ? url.slice(url.indexOf('?')) : '';
  return new URLSearchParams(query);
}

function NativeSearchPage({ url, settings, onOpen }: { url: string; settings: BrowserSettings; onOpen: (url: string) => void }) {
  const params = useMemo(() => searchParamsFromWebSurfUrl(url), [url]);
  const initialQuery = params.get('q') || '';
  const requestedEngine = (params.get('engine') || settings.searchProvider || 'google') as SearchProvider;
  const [query, setQuery] = useState(initialQuery);
  const [response, setResponse] = useState<SearchResponse | undefined>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const q = params.get('q') || '';
    const engine = (params.get('engine') || settings.searchProvider || 'google') as SearchProvider;
    setQuery(q);
    setResponse(undefined);
    setError('');
    if (!q) return () => { cancelled = true; };
    setLoading(true);
    fetch(searchApiUrl(engine, q))
      .then((res) => res.json() as Promise<SearchResponse>)
      .then((data) => {
        if (cancelled) return;
        setResponse(data);
        if (!data.results.length) setError(data.warning || 'No results were returned. Try a different query or switch search engines in Settings.');
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Search failed.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [params, settings.searchProvider]);

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!query.trim()) return;
    onOpen(`websurf://search?engine=${encodeURIComponent(requestedEngine)}&q=${encodeURIComponent(query.trim())}`);
  }

  return (
    <section className="internal-page search-results-page">
      <header className="page-header search-results-header">
        <div>
          <p className="eyebrow">websurf://search</p>
          <h1>Search results</h1>
        </div>
        <span className="pill">{SEARCH_ENGINES[requestedEngine]?.label || 'Search'}</span>
      </header>
      <form className="native-search-box" onSubmit={submit}>
        <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search the web" autoComplete="off" />
        <button type="submit">Search</button>
      </form>
      {loading && <div className="search-loading"><span className="tab-spinner" /> Searching the web…</div>}
      {response?.warning && <p className="search-warning">{response.warning}</p>}
      {error && !loading && <p className="search-warning">{error}</p>}
      <div className="search-result-list">
        {response?.results.map((result) => (
          <article className="search-result-card" key={`${result.url}:${result.title}`}>
            <button type="button" className="search-result-title" onClick={() => onOpen(result.url)}>{result.title || result.url}</button>
            <button type="button" className="search-result-url" onClick={() => onOpen(result.url)}>{toDisplayUrl(result.url)}</button>
            {result.snippet && <p>{result.snippet}</p>}
            <div className="search-result-actions">
              <button onClick={() => onOpen(result.url)}>Open in WebSurf</button>
              <button onClick={() => window.open(result.url, '_blank', 'noopener,noreferrer')}>Open in Chrome</button>
            </div>
          </article>
        ))}
      </div>
      {!loading && !response?.results.length && !error && (
        <p className="empty-copy">Type a search above or use the WebSurf URL bar.</p>
      )}
    </section>
  );
}

export function InternalPage({ page, currentUrl, bookmarks, settings, extensions, onBookmarksChange, onSettingsChange, onExtensionsChange, onOpen }: Props) {
  const [offlineCount, setOfflineCount] = useState(0);
  useEffect(() => { listOfflinePages().then((items) => setOfflineCount(items.length)); }, [page]);

  if (page === 'history') return <HistoryPage onOpen={onOpen} />;
  if (page === 'bookmarks') return <BookmarkManager root={bookmarks} onChange={onBookmarksChange} onOpen={onOpen} />;
  if (page === 'extensions') return <ExtensionsPage extensions={extensions} onChange={onExtensionsChange} />;
  if (page === 'passwords') return <PasswordManager />;
  if (page === 'settings') return <SettingsPage settings={settings} onChange={onSettingsChange} offlineCount={offlineCount} />;
  if (page === 'sandbox') return <HtmlSandbox />;
  if (page === 'search') return <NativeSearchPage url={currentUrl} settings={settings} onOpen={onOpen} />;
  return <HomePage settings={settings} onOpen={onOpen} />;
}
